<script lang="ts">
	import { getGlobalSettings } from "../getGlobalSettings.js";
	import { createPromiseStore } from "../../../core/utils/createPromiseStore.js";
	import type { ContactFormInput } from "../../../core/schema/ContactForm.js";
	import { getClients } from "../appsync/getClients.js";
	import sendContactForm from "../appsync/mutations/sendContactForm.graphql";
	import cake from "../../../core/assets/icons/cake.svg?raw";
	import LabelledInput from "./Form/LabelledInput.svelte";
	import GdprCheckbox from "./Form/GdprCheckbox.svelte";
	import Button from "./Button.svelte";
	import Modal from "./Modal.svelte";
	import Form from "./Form/Form.svelte";
	import Message from "./Message.svelte";

	const globalSettings = getGlobalSettings();
	const { TENANT_ID } = globalSettings;

	const send = createPromiseStore(async (input: ContactFormInput) => {
		const { graphQL } = await getClients(globalSettings);
		await graphQL.mutate<{ input: ContactFormInput }>({
			mutation: sendContactForm,
			variables: { input },
		});
	});

	let showSuccessModal = false;
	async function submit(event: SubmitEvent): Promise<void> {
		const form = event.target as HTMLFormElement;
		const formData = new FormData(form);
		const input: ContactFormInput = {
			firstName: String(formData.get("firstName")),
			lastName: String(formData.get("lastName")),
			email: String(formData.get("email")),
			phoneNumber: String(formData.get("phoneNumber")),
			message: String(formData.get("message")),
			tenantId: TENANT_ID,
			gdprApprovedAt: String(formData.get("gdprApprovedAt")),
		};
		await send.call(input);
		form.reset();
		showSuccessModal = true;
	}

	// eslint-disable-next-line no-warning-comments
	// FIXME remove this after upgrade to Svelte 5.
	/* eslint-disable @typescript-eslint/explicit-function-return-type */
</script>

<Modal show={showSuccessModal}>
	<p class="h2" slot="title">Děkujeme za zprávu</p>
	<div class="flex flex-col items-center gap-2">
		<p>Vaše zpráva byla úspěšně odeslána. Brzy se vám ozveme.</p>

		<Button
			on:click={() => {
				showSuccessModal = false;
			}}
		>
			Rozumím
		</Button>
	</div>
</Modal>

<Form on:submit={submit}>
	<div class="mt-12 grid w-full items-end gap-x-6 gap-y-3 lg:grid-flow-col lg:grid-rows-2">
		<LabelledInput id="firstName" required autocomplete="given-name">Jméno</LabelledInput>
		<LabelledInput id="lastName" required autocomplete="family-name">Příjmení</LabelledInput>
		<LabelledInput id="email" required type="email" autocomplete="email">E-mail</LabelledInput>
		<LabelledInput id="phoneNumber" required type="tel" autocomplete="tel">Telefon</LabelledInput>
	</div>
	<div class="relative mt-4 w-full">
		<textarea
			id="message"
			name="message"
			class="input-with-label peer h-[15.5625rem] w-full rounded-[.25rem] border border-gray-200 px-3 !pb-4 !pt-[2.25rem] text-base focus:font-semibold focus:outline-none lg:px-6"
			placeholder=" "
			required
			minlength="10"
		></textarea>
		<label class="label textarea-label" for="message">Vaše zpráva</label>
		<p class="text-2xs mt-3">*Povinné údaje</p>
	</div>

	<div class="flex flex-col items-center gap-8">
		<GdprCheckbox />

		<Button
			class="sm:!text-md mx-auto !rounded-full !px-8 !py-2 sm:!px-12 sm:!py-4 lg:mx-0 xl:!px-28 xl:!py-[1.125rem] xl:!text-[1.375rem]"
			type="submit"
			icon={cake}
			iconClass="h-6"
			disabled={$send.status === "loading"}
		>
			{#if $send.status === "loading"}
				Odesílám…
			{:else}
				Odeslat
			{/if}
		</Button>
	</div>

	{#if $send.status === "error"}
		<Message variant="error" class="mt-4">Něco se pokazilo, zkuste to prosím znovu a později.</Message>
	{/if}
</Form>
